import { FunctionComponent } from "react";

import { Toggle } from "@/components/Toggle/Toggle";
import { ListRecurrence, ListType } from "@/enum/list.enum";
import {
	useGetListRunEmailSubscriptionQuery,
	useSetListRunEmailSubscriptionMutation,
} from "@/pages/Private/redux/list/list-run-email.api";
import { classNames } from "@/utils/classNames";

import { runEmailToggleLabel } from "../utils/listScheduleLabels";

/**
 * "Send me a notification by email …" for one list (PREC-3908).
 *
 * The setting is per (list, user): the list's creator gets it on by default for one-time, weekly
 * and monthly lists and off for daily ones — a daily list would otherwise mail every morning —
 * while everyone else starts off and can switch it on for a list they did not create.
 *
 * Rendered under the run-frequency controls in list settings. It writes immediately rather than
 * joining the settings form: it is this user's own preference, not part of the list's shape, and
 * saving it should not require saving whatever else is half-edited on the screen.
 *
 * The label sits *beside* the toggle, not inside it: `Toggle`'s root is a fixed 38×22px pill and
 * renders `children` within it, so a sentence handed to it as a child wraps to one word per line
 * and overflows onto the settings block below. The click handler therefore lives on the row, which
 * keeps the label clickable now that it is no longer part of the toggle.
 */
export const ListRunEmailToggle: FunctionComponent<{
	listId: number;
	type: ListType | null | undefined;
	recurrence: ListRecurrence | null | undefined;
}> = ({ listId, type, recurrence }) => {
	const { data, isLoading } = useGetListRunEmailSubscriptionQuery(listId, { skip: !listId });
	const [setSubscription, { isLoading: isSaving }] = useSetListRunEmailSubscriptionMutation();

	const enabled = data?.enabled ?? false;
	const isBusy = isLoading || isSaving;

	const handleToggle = () => {
		if (isBusy) {
			return;
		}

		void setSubscription({ listId, enabled: !enabled });
	};

	return (
		<div
			className={classNames(
				"flex items-start gap-3 w-full mb-5",
				isBusy ? "cursor-not-allowed" : "cursor-pointer"
			)}
			onClick={handleToggle}
		>
			<Toggle
				className="mt-0.5"
				disabled={isBusy}
				isChecked={enabled}
				name="runEmailSubscription"
			/>

			<span className="text-sm text-gray-700">{runEmailToggleLabel(type, recurrence)}</span>
		</div>
	);
};
